<script setup lang="ts">
import dayjs from 'dayjs'

import CardTitle from '@/components/CardTitle.vue'
import AppTooltip from '@/components/ui/AppTooltip.vue'
import type { CustomerDetails } from '@/types/types'

const props = defineProps<{
  customer: CustomerDetails
  monthlyVisitsFrequency: number
}>()

const { merchantName } = useLocalStorage()
const { locale, t } = useI18n()
const { formatNumber } = useNumber()

const registerDateTime = computed(() => {
  return `${new Date(props.customer.registerDate).toLocaleDateString(locale.value)} ${new Date(
    props.customer.registerDate
  ).toLocaleTimeString(locale.value)}`
})

const lastValidCheckinDateTime = computed(() => {
  if (props.customer.lastValidCheckinDate === null) {
    return '-'
  }
  return `${new Date(props.customer.lastValidCheckinDate).toLocaleDateString(locale.value)} ${new Date(
    props.customer.lastValidCheckinDate
  ).toLocaleTimeString(locale.value)}`
})

const monthsSinceRegister = computed(() => {
  const elapsedMonths = dayjs().diff(dayjs(props.customer.registerDate), 'months')

  return elapsedMonths <= 0 ? 0 : elapsedMonths
})
</script>

<template>
  <div class="w-full rounded-lg border bg-white">
    <div class="ml-4 flex items-center py-5 sm:ml-6">
      <CardTitle
        :title="t('pages.customer.customerDetailPage.blocks.checkinsStats.title')"
        :subtitle="
          t(
            `pages.customer.customerDetailPage.blocks.checkinsStats.description.${['man', 'woman'].includes(customer.gender) ? customer.gender : 'unknown'}`,
            {
              merchantName: merchantName
            }
          )
        "
        name="HeroMapPinIconOutline"
      />
    </div>

    <div class="border-t px-4 py-5 sm:px-6">
      <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-4">
        <div class="sm:col-span-2">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.checkinsStats.registerStore') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.registerStore.name }}
          </dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="text-sm font-medium text-muted-foreground">
            <span class="inline-flex items-center gap-1">
              {{ t('pages.customer.customerDetailPage.blocks.checkinsStats.referenceStore') }}
              <AppTooltip :text="t('pages.customer.customerDetailPage.blocks.checkinsStats.referenceStoreTooltip')">
                <BaseIcon
                  name="IconoirInfoCircle"
                  class="inline-flex size-[1em] shrink-0 cursor-pointer text-current hover:text-muted-foreground focus-visible:text-muted-foreground"
                />
              </AppTooltip>
            </span>
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ customer.referenceStore.name }}
          </dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.checkinsStats.registerDate') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ registerDateTime }}
          </dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.checkinsStats.lastVisit') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ lastValidCheckinDateTime }}
          </dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.checkinsStats.checkinCount') }}
          </dt>
          <dd class="mt-1 text-sm text-foreground">
            {{ formatNumber(customer.checkinCount) }}
          </dd>
        </div>
        <div class="sm:col-span-2">
          <dt class="text-sm font-medium text-muted-foreground">
            {{ t('pages.customer.customerDetailPage.blocks.checkinsStats.visitsFrequency') }}
          </dt>
          <dd v-if="monthsSinceRegister <= 1" class="mt-1 text-sm text-foreground">
            {{ monthlyVisitsFrequency }} {{ t('global.words.visit', monthlyVisitsFrequency > 1 ? 2 : 1) }}
          </dd>
          <dd v-else class="mt-1 text-sm text-foreground">
            {{ monthlyVisitsFrequency }}
            {{ t('global.persona.monthlyVisitsFrequency', monthlyVisitsFrequency > 1 ? 2 : 1) }}
          </dd>
        </div>
      </dl>
    </div>
  </div>
</template>
